/**
 * Utility that manages throttled sync operations
 * Prevents a flood of sync requests by throttling them based on a configurable delay
 */
export class ThrottledSync {
    private lastSyncTime: number = 0;
    private pendingSync: NodeJS.Timeout | undefined = undefined;

    /**
     * @param syncThrottleMs - The minimum time between syncs in milliseconds
     */
    constructor(private syncFn: (projectId: string) => Promise<void> | void, private syncThrottleMs: number) {
    }

    /**
     * Syncs now or schedules a sync after the throttle period
     */
    public syncNowOrWait = async (projectId: string): Promise<void> => {
        // Check when the last sync was performed
        const now = Date.now();
        const timeSinceLastSync = now - this.lastSyncTime;

        // If there's already a pending sync for this id, do nothing
        if (this.pendingSync) {
            return;
        }

        if (timeSinceLastSync < this.syncThrottleMs) {
            // If we've synced recently, schedule a sync after the throttle period
            const waitTime = this.syncThrottleMs - timeSinceLastSync;

            this.pendingSync = setTimeout(async () => {
                this.pendingSync = undefined;
                this.lastSyncTime = Date.now();
                await this.syncFn(projectId);
            }, waitTime);
        } else {
            // Sync immediately
            this.pendingSync = setTimeout(async () => {
                this.pendingSync = undefined;
                this.lastSyncTime = Date.now();
                await this.syncFn(projectId);
            }, 100);
        }
    }
}